import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLayout } from '../contexts/LayoutContext';
import { mapFarms, filterStations, getStationCounts } from '../utils/mapMockData';
import { MapControlsBar } from '../components/map/MapControlsBar';
import { LeafletMap } from '../components/map/LeafletMap';
import { StatusSummaryCard } from '../components/map/StatusSummaryCard';
import { StationListPanel } from '../components/map/StationListPanel';
import '../styles/leaflet-overrides.css';
import './MapPage.css';

export function MapPageView() {
  const { selectedFarm: contextFarm, selectFarm, sidebarCollapsed, setSidebarCollapsed } = useLayout();
  const location = useLocation();

  // Find current farm from map data
  const currentFarm = mapFarms.find(f => f.id === contextFarm?.id || f.name === contextFarm?.name) || mapFarms[0];

  const [filters, setFilters] = useState({
    status: [], // Empty means all
    stationType: 'all',
    searchQuery: ''
  });

  const [selectedStation, setSelectedStation] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false); // Start with panel CLOSED for max map space
  const [mapKey, setMapKey] = useState(0); // Force map re-render on farm change

  // Get filtered stations
  const filteredStations = filterStations(currentFarm.stations, filters);
  const stationCounts = getStationCounts(currentFarm.stations);

  // Force map update when farm changes
  useEffect(() => {
    setMapKey(prev => prev + 1);
    setSelectedStation(null);
  }, [currentFarm.id]);

  // Handle deep linking from other pages
  useEffect(() => {
    if (location.state?.stationId) {
      // Search for station across all farms
      let foundStation = null;
      let foundFarm = null;

      for (const farm of mapFarms) {
        const station = farm.stations.find(s => s.id === location.state.stationId);
        if (station) {
          foundStation = station;
          foundFarm = farm;
          break;
        }
      }

      if (foundStation && foundFarm) {
        // If station is in a different farm, switch farm first
        if (foundFarm.id !== currentFarm.id) {
          selectFarm(foundFarm);
        }

        setSelectedStation(foundStation);
        setPanelOpen(false); // Keep panel closed to show map view
        
        // Center map on the selected station after a short delay to ensure map is ready
        // Use a longer delay to ensure map is fully rendered
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('map:center-station', { 
            detail: { 
              lat: foundStation.locationLat, 
              lon: foundStation.locationLon,
              stationId: foundStation.id
            } 
          }));
        }, 500);
        
        // Clear state to prevent re-opening on re-renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, currentFarm.id, selectFarm]);

  // Close station list panel when heatmap dropdown opens
  useEffect(() => {
    const handleHeatmapDropdownOpen = () => {
      if (panelOpen) {
        setPanelOpen(false);
      }
    };

    window.addEventListener('map:heatmap-dropdown-open', handleHeatmapDropdownOpen);
    return () => window.removeEventListener('map:heatmap-dropdown-open', handleHeatmapDropdownOpen);
  }, [panelOpen]);

  // Close station list panel when layers dropdown opens
  useEffect(() => {
    const handleLayersDropdownOpen = () => {
      if (panelOpen) {
        setPanelOpen(false);
      }
    };

    window.addEventListener('map:layers-dropdown-open', handleLayersDropdownOpen);
    return () => window.removeEventListener('map:layers-dropdown-open', handleLayersDropdownOpen);
  }, [panelOpen]);

  const handleStationClick = (station) => {
    setSelectedStation(station);
    setPanelOpen(false);
    // Center map on the station with a single dispatch
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('map:center-station', { 
        detail: { 
          lat: station.locationLat, 
          lon: station.locationLon,
          stationId: station.id
        } 
      }));
    });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return (
    <div className={`map-page ${sidebarCollapsed ? 'map-page--sidebar-collapsed' : ''}`}>
      {/* Map Controls Bar */}
      <MapControlsBar
        farm={currentFarm}
        filters={filters}
        onFilterChange={handleFilterChange}
        sidebarCollapsed={sidebarCollapsed}
      />

      {/* Leaflet Map */}
      <LeafletMap
        key={mapKey}
        farm={currentFarm}
        stations={filteredStations}
        selectedStation={selectedStation}
        onStationClick={handleStationClick}
        panelOpen={panelOpen}
      />

      {/* Status Summary Card */}
      <StatusSummaryCard
        farm={currentFarm}
        counts={stationCounts}
      />

      {/* Station List Panel */}
      <StationListPanel
        farm={currentFarm}
        stations={filteredStations}
        selectedStation={selectedStation}
        isOpen={panelOpen}
        onToggle={() => setPanelOpen(!panelOpen)}
        onStationClick={handleStationClick}
      />
    </div>
  );
}